import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useAppDispatch } from '@store';
import { clearInventory } from '@features/inventory';
import { clearSupplyChain } from '@features/supplyChain';
import {
  Credentials,
  ICredentialsService,
  TokenCredentials,
  User,
} from './types';
import { FilterService } from './filterService';
import { inboundProcessesAtom, outboundProcessesAtom } from '../store/jotai';
import { forgetBookingSource } from '@utils';
import { FLOX_KNOWN_BROWSER_KEY } from '@constants';

export function useCredentialsService(): ICredentialsService {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const setInboundProcesses = useSetAtom(inboundProcessesAtom);
  const setOutboundProcesses = useSetAtom(outboundProcessesAtom);

  const { clearFilters } = FilterService();

  const [accessToken, setAccessToken] = useState(
    localStorage.getItem('accessToken')
  );

  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem('refreshToken')
  );

  const [notificationToken, setNotificationToken] = useState(
    localStorage.getItem('notificationToken')
  );

  const [tokenExpiry, setTokenExpiry] = useState(
    localStorage.getItem('tokenExpiry')
  );

  const [user, setUser] = useState<User | null>(
    JSON.parse(localStorage.getItem('user') as string)
  );

  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(localStorage.getItem('isAuthenticated'))
  );

  const setAuthenticated = (authStatus: boolean) => {
    if (authStatus) {
      localStorage.setItem('isAuthenticated', authStatus.toString());
    } else {
      localStorage.removeItem('isAuthenticated');
    }

    setIsAuthenticated(authStatus);
  };

  const getCredentials = (): Credentials => {
    return {
      accessToken,
      refreshToken,
      tokenExpiry,
      notificationToken,
      isAuthenticated,
    };
  };

  const setCredentials = (
    tokenCredentials: TokenCredentials,
    authStatus: boolean
  ) => {
    localStorage.setItem('accessToken', tokenCredentials.accessToken);
    localStorage.setItem('refreshToken', tokenCredentials.refreshToken);
    localStorage.setItem('tokenExpiry', tokenCredentials.tokenExpiry);
    localStorage.setItem('notificationToken', tokenCredentials.notificationToken);

    setAccessToken(tokenCredentials.accessToken);
    setRefreshToken(tokenCredentials.refreshToken);
    setTokenExpiry(tokenCredentials.tokenExpiry);
    setNotificationToken(tokenCredentials.notificationToken);
    setAuthenticated(authStatus);
  };

  const deleteCredentials = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('notificationToken');
    forgetBookingSource();
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiry(null);
    setNotificationToken(null);
    setAuthenticated(false);
  };

  const logIn = (
    userCredentials: User,
    auth: TokenCredentials,
    url: string
  ) => {
    flushSync(() => {
      localStorage.setItem('user', JSON.stringify(userCredentials));
      // Mark this browser as "known" so /register can show the welcome-back
      // screen on the next visit. Replaces the prior server-set
      // `flox_known_user` HttpOnly cookie.
      localStorage.setItem(FLOX_KNOWN_BROWSER_KEY, '1');
      setCredentials(
        {
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          tokenExpiry: auth.tokenExpiry,
          notificationToken: auth.notificationToken,
        },
        true
      );
    });
    navigate(url);
  };

  const logOut = () => {
    dispatch(clearInventory());
    dispatch(clearSupplyChain());
    deleteCredentials();
    setUser(null);
    clearFilters();
    setInboundProcesses([]);
    setOutboundProcesses([]);
    navigate('/login');
  };

  const forgotPassword = () => {
    navigate('/forgotten-password');
  };

  return {
    user,
    setUser,
    getCredentials,
    setCredentials,
    deleteCredentials,
    isAuthenticated,
    logIn,
    logOut,
    forgotPassword,
  };
}
